// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Promotions & banners  (the banner cards staff publish, shown on Home and the Menu)
// Fetches GET /public/banners?placement=… — only banners that are live, inside their window and (when they advertise a code) whose code still works are returned by the backend. The audience
// (everyone / guests / signed-in) is applied here because the public endpoint doesn't know who is asking. Renders nothing while loading, on error or when there are none, so a quiet
// week leaves no empty gap. Static cards (no auto-rotating carousel): nothing moves, everything is reachable by keyboard; images carry the alt text staff wrote and their credit.
// Neighbours: pages/Home, pages/Menu, utils/promotions, pages/admin/AdminBanners (the editor + live preview reuse BannerCard).
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Tag } from "lucide-react";
import useAuth from "../hooks/useAuth";
import { fetchPublicBanners, type BannerPlacement, type BannerTheme, type PublicBanner } from "../utils/promotions";

const THEMES: Record<BannerTheme, string> = {
  ocean: "bg-ocean-800 text-foam-50",
  teal: "bg-teal-700 text-white",
  coral: "bg-foam-50 text-ocean-950 ring-2 ring-coral-500/60",
};

/** One banner card. `preview` renders it without a working link (the admin editor). */
export function BannerCard({ banner, preview = false }: { banner: Omit<PublicBanner, "id" | "placement" | "audience"> & { id?: string }; preview?: boolean }) {
  const body = (
    <>
      {banner.imageUrl && (
        <>
          <img src={banner.imageUrl} alt={banner.imageAlt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-ocean-950/90 via-ocean-950/65 to-ocean-950/20" />
        </>
      )}
      <div className={`relative flex h-full min-h-40 flex-col justify-end gap-2 p-5 ${banner.imageUrl ? "text-foam-50" : ""}`}>
        {banner.promoCode && (
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-foam-50 px-3 py-1 text-xs font-bold text-ocean-950">
            <Tag className="h-3.5 w-3.5" aria-hidden="true" /> Code <span className="font-mono tracking-wider">{banner.promoCode}</span>
          </span>
        )}
        <h3 className="font-display text-xl font-bold leading-tight">{banner.title}</h3>
        {banner.subtitle && <p className="text-sm opacity-90">{banner.subtitle}</p>}
        {banner.ctaLabel && (
          <span className="mt-1 inline-flex items-center gap-1.5 text-sm font-bold underline underline-offset-4">
            {banner.ctaLabel} <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </span>
        )}
        {banner.imageUrl && banner.imageCredit && <span className="text-[11px] opacity-70">Photo: {banner.imageCredit}</span>}
      </div>
    </>
  );
  const frame = `relative block overflow-hidden rounded-2xl shadow-sm ${THEMES[banner.theme]}`;
  return preview ? <div className={frame}>{body}</div> : <Link to={banner.link} data-testid="promo-banner" className={`${frame} focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500`}>{body}</Link>;
}

export default function PromoBanners({ placement, className = "" }: { placement: BannerPlacement; className?: string }) {
  const auth = useAuth();
  const [banners, setBanners] = useState<PublicBanner[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchPublicBanners(placement).then((list) => !cancelled && setBanners(list), () => undefined);
    return () => { cancelled = true; };
  }, [placement]);

  if (auth.status === "loading") return null;
  const signedIn = auth.status === "signed-in";
  const shown = banners.filter((b) => b.audience === "all" || (b.audience === "signed_in" ? signedIn : !signedIn)).slice(0, 3);
  if (shown.length === 0) return null;
  return (
    <section aria-label="Offers and announcements" className={className}>
      <div className={`grid gap-4 ${shown.length > 1 ? "md:grid-cols-2" : ""} ${shown.length > 2 ? "lg:grid-cols-3" : ""}`}>
        {shown.map((b) => <BannerCard key={b.id} banner={b} />)}
      </div>
    </section>
  );
}
